import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import FirebaseActions from '~redux/Auth/actions';
import { shouldRefreshToken, shouldRefreshTokenOnResume, TOKEN_CHECK_MS } from '~utils/auth';

const isOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false);

export default function useRefreshFirebaseToken(logged, tokenManager) {
  const dispatch = useDispatch();
  const lastRefreshAtRef = useRef(null);

  useEffect(() => {
    if (!logged) return undefined;

    const refresh = () => {
      lastRefreshAtRef.current = Date.now();
      dispatch(FirebaseActions.refreshFirebaseToken(tokenManager.refreshToken));
    };

    // Both entry points share the gate: a resume next to a timer tick would otherwise refresh twice.
    const refreshIfDue = () => {
      if (
        shouldRefreshToken({
          isOnline: isOnline(),
          lastRefreshAt: lastRefreshAtRef.current,
          now: Date.now()
        })
      ) {
        refresh();
      }
    };

    // Chrome throttles timers in background tabs, so the timer alone comes back with a stale token.
    const handleResume = () => {
      const shouldRefresh = shouldRefreshTokenOnResume({
        isVisible: document.visibilityState === 'visible',
        isOnline: isOnline(),
        lastRefreshAt: lastRefreshAtRef.current,
        now: Date.now()
      });

      if (shouldRefresh) refresh();
    };

    const interval = setInterval(refreshIfDue, TOKEN_CHECK_MS);
    window.addEventListener('focus', handleResume);
    window.addEventListener('online', handleResume);
    document.addEventListener('visibilitychange', handleResume);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleResume);
      window.removeEventListener('online', handleResume);
      document.removeEventListener('visibilitychange', handleResume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logged]);
}
